/**
 * platform.ts — lightweight client-platform detection.
 *
 * Apple Sign-In is offered ONLY on iOS / Safari per the locked auth model
 * (memory project_unified_login: "Apple Sign-In button shown only on iOS and
 * Safari per Apple branding rules; Google button universal") and Apple's
 * Human Interface / branding guidelines (don't surface "Sign in with Apple"
 * on platforms where it adds nothing). Google is universal.
 *
 * Pure core (`detectAppleSignIn`) takes the UA string + vendor so it is
 * unit-testable without a browser; the thin `appleSignInAvailable()` wrapper
 * reads `navigator`. SSR-safe (no window → false).
 */

/**
 * PURE — decide whether to show the Apple sign-in button from a UA string +
 * the navigator vendor. Returns true for iOS (iPhone/iPad/iPod, incl. modern
 * iPadOS that reports as Macintosh + touch) and desktop Safari; false for
 * Chrome/Firefox/Edge on non-Apple platforms.
 *
 * @param ua        navigator.userAgent
 * @param vendor    navigator.vendor (Safari reports "Apple Computer, Inc.")
 * @param maxTouch  navigator.maxTouchPoints (iPadOS-on-desktop-UA tell)
 */
export function detectAppleSignIn(
  ua: string,
  vendor: string,
  maxTouch: number,
): boolean {
  const u = (ua || '').toLowerCase();

  // Explicit iOS devices.
  if (/iphone|ipad|ipod/.test(u)) return true;

  // iPadOS 13+ reports a desktop "Macintosh" UA but exposes touch — treat a
  // touch-capable Mac UA as an iPad.
  const isMacUA = /macintosh|mac os x/.test(u);
  if (isMacUA && maxTouch > 1) return true;

  // Desktop Safari: WebKit UA, vendor "Apple Computer, Inc.", and NOT one of
  // the Chromium/Gecko engines that also carry "safari" in their UA.
  const isSafariUA =
    /safari/.test(u) &&
    !/chrome|chromium|crios|android|edg|edgios|firefox|fxios|opr|opera/.test(u);
  const isAppleVendor = /apple/i.test(vendor || '');
  if (isSafariUA && isAppleVendor) return true;

  return false;
}

/** Is Apple sign-in worth offering on this client? SSR-safe. */
export function appleSignInAvailable(): boolean {
  if (typeof navigator === 'undefined') return false;
  return detectAppleSignIn(
    navigator.userAgent || '',
    navigator.vendor || '',
    typeof navigator.maxTouchPoints === 'number'
      ? navigator.maxTouchPoints
      : 0,
  );
}
